import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./stylesPoker.css";

import { Drawer } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";
import { handleExpiredTokenError } from "../../../../utils/ErrorUtils";
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import { formatDateNoTimezone } from "../../../../utils/dateService";
import { formatCurrency } from "../../../../utils/financialFormatters";

function HandTable({
  userData,
  handData,
  setActiveSection,
  currencySymbol,
}) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken
  );
  const navigate = useNavigate();
  const [hands, setHands] = useState([]);
  const [handDetailsItems, setHandDetailsItems] = useState([]);
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 50,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });

  const formatCard = (card) => {
    if (!card) return "-";
    const rankMap = {
      1: "A",
      11: "J",
      12: "Q",
      13: "K",
    };
    const rank = rankMap[card.rank] || card.rank;
    return `${rank}${card.suit || ""}`;
  };

  const formatCards = (cards) =>
    Array.isArray(cards) && cards.length
      ? cards.map(formatCard).join(" ")
      : "-";

  const formatMoney = (value) =>
    value == null || value === "-"
      ? "-"
      : formatCurrency(Number(value), currencySymbol, 2);

  const columns = [
    {
      title: AppStrings.HAND_ID,
      dataIndex: "hand_id",
      key: "hand_id",
      width: 150,
    },
    {
      title: AppStrings.ROUND_START_TIME,
      dataIndex: "round_start_time",
      key: "round_start_time",
      width: 150,
      render: (text) =>
        text && text !== "-"
          ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")
          : "-",
    },
    {
      title: AppStrings.ROUND_END_TIME,
      dataIndex: "round_end_time",
      key: "round_end_time",
      width: 150,
      render: (text) =>
        text && text !== "-"
          ? formatDateNoTimezone(text, "DD MMMM YYYY, HH:mm:ss")
          : "-",
    },
    {
      title: AppStrings.STATUS,
      dataIndex: "status",
      key: "status",
      width: 150,
    },
    {
      title: AppStrings.BET_AMOUNT,
      dataIndex: "bet_amount",
      key: "bet_amount",
      width: 150,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {formatMoney(text)}
          </p>
        );
      },
    },
    {
      title: AppStrings.PAYOUT,
      dataIndex: "payout",
      key: "payout",
      width: 150,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {formatMoney(text)}
          </p>
        );
      },
    },
    {
      title: AppStrings.PLAYER_CARDS,
      dataIndex: "player_cards",
      key: "player_cards",
      width: 200,
      render: formatCards,
    },
  ];

  const [open, setOpen] = useState(false);

  const getHandNumber = (handId) => {
    if (!handId) return null;
    const parts = String(handId).split(":");
    return parts.length > 1 ? Number(parts[parts.length - 1]) : null;
  };

  const buildHandDetailsItems = (details) => {
    const items = [
      { title: AppStrings.HAND_ID, value: details?.hand_id || "-", width: "32%" },
      {
        title: AppStrings.START_TIME,
        value:
          details?.start_time
            ? formatDateNoTimezone(details.start_time, "DD MMMM YYYY, HH:mm:ss")
            : "-",
        width: "32%",
      },
      {
        title: AppStrings.ROUND_END_TIME,
        value:
          details?.end_time
            ? formatDateNoTimezone(details.end_time, "DD MMMM YYYY, HH:mm:ss")
            : "-",
        width: "32%",
      },
      { title: AppStrings.BET_AMOUNT, value: formatMoney(details?.bet_amount), width: "32%" },
      { title: AppStrings.PAYOUT, value: formatMoney(details?.payout_amount), width: "32%" },
      { title: AppStrings.STATUS, value: details?.ticket_status || "-", width: "32%" },
      { title: AppStrings.PLAYER_CARDS, value: formatCards(details?.player_cards), width: "32%" },
      {
        title: AppStrings.PLAYER_HAND_COMBINATION,
        value: details?.player_hand_combination || "-",
        width: "32%",
      },
      { title: AppStrings.DECISION, value: details?.decision || "-", width: "32%" },
    ];

    (details?.other_players || []).forEach((player) => {
      items.push(
        {
          title: AppStrings.LIST_EVERY_OTHER_PLAYER,
          value: player?.user_id || "-",
          width: "32%",
        },
        {
          title: AppStrings.CARDS,
          value: formatCards(player?.cards),
          width: "32%",
        },
        {
          title: AppStrings.HAND_COMBINATION,
          value: player?.hand_combination || "-",
          width: "32%",
        }
      );
    });

    (details?.winners || []).forEach((winner, index) => {
      const winnerTitle =
        index === 0
          ? AppStrings.WINNER_1
          : index === 1
          ? AppStrings.WINNER_2
          : `Winner ${index + 1}`;
      const payoutTitle =
        index === 0
          ? AppStrings.WINNER_1_PAYOUT
          : index === 1
          ? AppStrings.WINNER_2_PAYOUT
          : `Winner ${index + 1} Payout`;

      items.push(
        { title: winnerTitle, value: winner?.user_id || "-", width: "49%" },
        {
          title: payoutTitle,
          value: formatMoney(winner?.payout_amount),
          width: "49%",
        }
      );
    });

    items.push({
      title: AppStrings.COMMUNITY_CARDS,
      value: formatCards(details?.community_cards),
      width: "100%",
    });

    return items;
  };

  const fetchHands = async (page = 1) => {
    if (!userData?.user_id || !handData?.game_id) {
      return;
    }
    setIsApiRunning(true);
    const params = {
      user_id: userData?.user_id,
      game_id: handData?.game_id,
      page: page,
      limit: pagination.page_size,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_USER_POKER_HANDS?.replace(
          "{user_id}",
          userData?.user_id
        )?.replace("{game_id}", handData?.game_id),
        params
      );
      const responseData = response?.data?.data;
      setHands(responseData?.data || []);
      const apiPagination = responseData?.pagination || {};
      setPagination({
        current_page: apiPagination?.current_page ?? page,
        page_size: apiPagination?.per_page ?? pagination.page_size,
        total_results: apiPagination?.total_hands ?? 0,
        total_pages: apiPagination?.total_pages ?? 0,
        has_next: apiPagination?.has_next ?? false,
        has_prev: apiPagination?.has_previous ?? false,
      });
    } catch (error) {
      console.log("error fetching poker hands", error?.response);
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsApiRunning(false);
    }
  };

  const fetchHandDetails = async (hand) => {
    const handNumber = getHandNumber(hand?.hand_id);
    if (!userData?.user_id || !handData?.game_id || !handNumber) {
      return;
    }
    setIsApiRunning(true);
    const params = {
      user_id: userData?.user_id,
      game_id: handData?.game_id,
      hand_number: handNumber,
    };

    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_USER_POKER_HAND_DETAILS?.replace(
          "{user_id}",
          userData?.user_id
        )
          ?.replace("{game_id}", handData?.game_id)
          ?.replace("{hand_number}", handNumber),
        params
      );
      const details = response?.data?.data;
      setHandDetailsItems(buildHandDetailsItems(details));
      setOpen(true);
    } catch (error) {
      console.log("error fetching poker hand details", error?.response);
      handleExpiredTokenError(navigate, error?.response);
    } finally {
      setIsApiRunning(false);
    }
  };

  useEffect(() => {
    fetchHands(1);
  }, [userData, handData?.game_id]);

  return (
    <div className="poker-bets-container">
      <Drawer
        title={AppStrings.HAND_DETAILS}
        closable={{ "aria-label": "Close Button" }}
        onClose={() => setOpen(false)}
        open={open}
        width={800}
      >
        <div className="hand-details-drawer-content">
          {handDetailsItems.map((item) => (
            <div
              key={`${item.title}-${item.value}`}
              className="hand-details-drawer-item"
              style={{ width: item.width }}
            >
              <span className="hand-details-drawer-item-title">
                {item.title}
              </span>
              <div className="hand-details-drawer-item-value">{item.value}</div>
            </div>
          ))}
        </div>
      </Drawer>
      <div className="poker-bets-header"></div>
      <div className="poker-bets-table-container">
        <div className="head-controls poker-bets-table-header-container">
          <div
            className="back-button-container"
            data-testid="back-button-container"
            onClick={() => setActiveSection("Poker Table")}
          >
            <LeftOutlined style={{ color: "white" }} />
          </div>

          <div className="header-text-container">
            <p className="header-text">{AppStrings.USERNAME_GAME_HANDS?.replace("{username}", userData?.username)}</p>
          </div>
        </div>

        <div className="">
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <div className="poker-bets-table">
              <DataTable
                dataSource={hands}
                columns={columns}
                scrollPreset="wide"
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    fetchHandDetails(record);
                  },
                  className: "poker-bets-table-row",
                })}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default HandTable;
